// The words an address carries from page to page of the site.
//
// A visitor can arrive with words in the address that are read later than the
// page they land on: a partner's code (`?ref=`), read when the demo form is sent,
// and an ad's click (`?gclid=` & co. and the campaign's `utm_*`), kept only once
// the visitor allows the site's measurement - which they may do on the third
// page, not the first. Nothing may be stored before that answer, and the
// partner's code is never stored at all, so the address is the only place either
// can live until then. The site's own links carry them on, as the move to a
// visitor's language does (main.ts) and every redirect of the edge does
// (deploy/Caddyfile). Nothing is kept on the device, and nothing goes anywhere
// new: another site's link is never touched, and the referrer policy hands other
// sites only this origin.
//
// This half reads no DOM and is covered by carry.test.ts; main.ts decides when
// which words travel.

/** A partner's share code: carried on every page, whatever the visitor answers. */
export const PARTNER_WORDS = ['ref'] as const;

/** An ad's click and campaign: carried only while the measurement question is unanswered. */
export const AD_WORDS = ['gclid', 'gbraid', 'wbraid', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;

/** The named words this address carries, in the order named; a word with no value is not one. */
export function wordsIn(search: string, names: readonly string[]): [string, string][] {
  const params = new URLSearchParams(search);
  const out: [string, string][] = [];
  for (const name of names) {
    const value = params.get(name);
    if (value) out.push([name, value]);
  }
  return out;
}

/**
 * The link with the words added, or null when it is not a link to carry them
 * onto: another origin, the api, or a place in this same document (the address
 * bar already holds the words). A word the link names itself is left as the
 * link names it.
 */
export function carriedHref(href: string, here: string, words: readonly (readonly [string, string])[]): string | null {
  if (!words.length) return null;
  let url: URL;
  let at: URL;
  try {
    url = new URL(href, here);
    at = new URL(here);
  } catch {
    return null;
  }
  if (url.origin !== at.origin || (url.protocol !== 'https:' && url.protocol !== 'http:')) return null;
  if (url.pathname.startsWith('/api/')) return null;
  if (url.pathname === at.pathname && url.search === at.search) return null;
  for (const [name, value] of words) {
    if (!url.searchParams.has(name)) url.searchParams.set(name, value);
  }
  return url.href;
}
